import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useUsedExercises, useExerciseProgress, type UsedExercise } from "@/hooks/useApi";

export default function ProgressPage() {
  const { data: exercises = [], isLoading } = useUsedExercises();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const filtered = exercises.filter((e) =>
    e.name.toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) return <div className="p-4 text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="p-4 space-y-5">
      <h1 className="text-xl font-semibold">Progress</h1>

      {exercises.length === 0 ? (
        <p className="text-sm text-zinc-500">No sessions logged yet.</p>
      ) : (
        <>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-zinc-600"
          />
          <div className="space-y-2">
            {filtered.map((ex) => (
              <ExerciseRow
                key={ex.id}
                exercise={ex}
                isExpanded={expanded === ex.id}
                onToggle={() => setExpanded(expanded === ex.id ? null : ex.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ExerciseRow({
  exercise,
  isExpanded,
  onToggle,
}: {
  exercise: UsedExercise;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { data: progress = [] } = useExerciseProgress(isExpanded ? exercise.id : "");

  const chartData = progress.map((p) => ({
    date: new Date(p.started_at).toLocaleDateString("en", { month: "short", day: "numeric" }),
    weight: p.max_weight,
    volume: Math.round(p.volume),
  }));

  return (
    <div className="rounded-lg border border-zinc-800">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between p-3"
      >
        <div className="text-left">
          <p className="text-sm font-medium">{exercise.name}</p>
          <p className="text-xs text-zinc-500">
            {exercise.muscle_group}
            {exercise.target ? ` · ${exercise.target}` : ""}
            {exercise.equipment ? ` · ${exercise.equipment}` : ""}
            {" · "}
            {exercise.session_count} session{exercise.session_count !== 1 ? "s" : ""}
            {exercise.max_weight != null ? ` · best ${exercise.max_weight}kg` : ""}
          </p>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-zinc-600 shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-zinc-600 shrink-0" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-zinc-800 p-3 space-y-5">
          {chartData.length === 0 ? (
            <p className="text-sm text-zinc-500">No data.</p>
          ) : (
            <>
              <Chart data={chartData} dataKey="weight" label="Max Weight (kg)" color="var(--color-brand)" />
              <Chart data={chartData} dataKey="volume" label="Volume (reps × kg)" color="#71717a" />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Chart({
  data,
  dataKey,
  label,
  color,
}: {
  data: { date: string; weight?: number; volume?: number }[];
  dataKey: string;
  label: string;
  color: string;
}) {
  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-zinc-400">{label}</p>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
          <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 11 }} />
          <YAxis tick={{ fill: "#71717a", fontSize: 11 }} width={36} />
          <Tooltip
            contentStyle={{
              backgroundColor: "#18181b",
              border: "1px solid #27272a",
              borderRadius: 6,
              color: "#f4f4f5",
              fontSize: 12,
            }}
          />
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={2}
            dot={{ fill: color, r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
